// src/pages/league-dashboard/Waivers.js
import React, { useState, useEffect, useCallback } from 'react';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import { RefreshCw, Clock, AlertCircle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import DropDesignationModal from '../../components/modals/DropDesignationModal';
import PlayerLink from '../../components/PlayerLink';
// Phase A.4.5 queued-plan wire-up (mirrors FreeAgentSearch.js + VirtualPlayerTable.js):
// give the Waivers page the same conditional-drop + rearrange-roster capabilities
// that FA Search has. The backend persist (waiver_service.py:399-426), award
// execution (waiver_service.py:660-676 → _execute_queued_plan → RosterPlanExecutor),
// schema (migration 024_add_waiver_claims_roster_plan_steps.sql), modal capability
// (DropDesignationModal.js:316-324 rearrange button), and helper (canClaimWaiverPlayer)
// were all built in commit e4300f17 (May 13). FA Search invokes them via
// usePlayerAssignment + useRosterPlan + RosterPlanModal; Waivers.js never got
// migrated. This file does that migration.
import RosterPlanModal from '../../components/league-dashboard/RosterPlanModal';
import { canClaimWaiverPlayer } from '../../utils/RosterCapacityUtils';
import {
  usePlayerAssignment,
  useRosterPlan,
} from './free-agent-search/hooks';

/**
 * Waivers page — Phase 5 waiver system.
 *
 * Props:
 *   leagueId    — string
 *   userTeamId  — string (the user's own team_id)
 *   league      — league object (passed through for future use)
 *
 * All player field access uses canonical paths ONLY.
 * No flat field access. No JSON.parse().
 */
const Waivers = ({ leagueId, userTeamId, league, userTeam, highlightClaimId = null }) => {
  // ========================================
  // STATE
  // ========================================
  const [waivers, setWaivers] = useState(null);      // { players_on_waivers, priority_order }
  const [myClaims, setMyClaims] = useState(null);    // { claims, team_priority }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modal state
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [playerPendingClaim, setPlayerPendingClaim] = useState(null);
  const [cancellingClaimId, setCancellingClaimId] = useState(null);

  // Notification deep-link (Bible § 4.5.12.2): once claims load, scroll to + highlight
  // the claim row named by ?claimId. No-op when absent — no wrong-row fallback.
  useEffect(() => {
    if (!highlightClaimId || !myClaims?.claims?.length) return;
    const el = document.getElementById(`claim-${highlightClaimId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightClaimId, myClaims]);

  // Phase A.4.5: rosterCapacityAnalysis + currentRoster come from
  // usePlayerAssignment (parity with FreeAgentSearch.js:149). It loads from
  // /my-roster and filters former/inactive at the hook layer (L67-71). Hook
  // also owns the canonical roster-capacity analyzer used by the canClaim
  // helper + the RosterPlanModal candidate flow.
  const positionAssignment = usePlayerAssignment(leagueId, league, {
    transactionsEnabled: true,
    isCommissionerMode: false,
    getTargetTeamId: () => null,
  });
  // Phase A.4.5: drop-shuffle plan flow (parity with FreeAgentSearch.js:161).
  // Plan state machine; opens RosterPlanModal; produces selectedDropPlayer +
  // selectedPlan that handleWaiverPlanQueueConfirm submits with the claim.
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: positionAssignment.playerForAssignment,
    currentRoster: positionAssignment.currentRoster,
    isCommissionerMode: false,
    getTargetTeamId: () => null,
  });
  // Phase A.4.5: waiver-claim queue mode (parity with FreeAgentSearch.js:175-176).
  // When true, RosterPlanModal's onConfirm submits a waiver claim with the
  // chosen plan instead of executing the plan immediately.
  const [isWaiverPlanQueueMode, setIsWaiverPlanQueueMode] = useState(false);
  const [waiverPlayerForQueue, setWaiverPlayerForQueue] = useState(null);

  // ========================================
  // DATA LOADING
  // ========================================
  const loadData = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    setError('');
    try {
      const [waiversRes, claimsRes] = await Promise.all([
        leaguesAPI.getLeagueWaivers(leagueId),
        leaguesAPI.getMyWaiverClaims(leagueId),
      ]);
      // Phase A.4.5: loadRosterCapacity populates positionAssignment.currentRoster
      // (filtered to non-former/non-inactive per usePlayerAssignment.js:67-71) +
      // rosterCapacityAnalysis (the position+bench breakdown that
      // canClaimWaiverPlayer needs). Mirrors FreeAgentSearch.js:253.
      await positionAssignment.loadRosterCapacity();
      // getLeagueWaivers and getMyWaiverClaims return raw axios responses
      setWaivers(waiversRes?.data || null);
      setMyClaims(claimsRes?.data || null);
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.error?.message ||
        data?.detail ||
        'Failed to load waiver data.'
      );
      console.error('Waivers load error:', err);
    } finally {
      setLoading(false);
    }
    // positionAssignment is stable across renders (hook-owned); referencing
    // its loadRosterCapacity directly creates a fresh closure each render but
    // the hook's underlying callback is memoized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ========================================
  // CLAIM SUBMIT
  // ========================================
  const handleClaimButtonClick = (player) => {
    setPlayerPendingClaim(player);
    setShowClaimModal(true);
  };

  // Phase A.4.5: open the RosterPlanModal in waiver-queue mode when the user
  // chooses to designate a rearrangement plan (the "Rearrange roster to put
  // in active {position}" button in DropDesignationModal). Parity with
  // FreeAgentSearch.js:178-184.
  const handleWaiverRequestPlan = useCallback((waiverPlayer) => {
    if (!waiverPlayer) return;
    setShowClaimModal(false);  // close DropDesignationModal
    setWaiverPlayerForQueue(waiverPlayer);
    positionAssignment.setPlayerForAssignment(waiverPlayer);
    setIsWaiverPlanQueueMode(true);
    rosterPlan.open();
  }, [positionAssignment, rosterPlan]);

  // Phase A.4.5: submit the claim with the plan_* fields after the user
  // picks a drop+plan from RosterPlanModal. Backend
  // waiver_service._resolve_queued_plan_to_json persists the steps as
  // roster_plan_steps JSONB on the claim; process_waiver_claims executes
  // them atomically at award time via _execute_queued_plan. Parity with
  // FreeAgentSearch.js:186-227.
  const handleWaiverPlanQueueConfirm = useCallback(async () => {
    if (!waiverPlayerForQueue || !rosterPlan.selectedPlan || !rosterPlan.selectedDropPlayer) {
      return null;
    }
    const leaguePlayerId = waiverPlayerForQueue.ids?.league_player;
    const dropLeaguePlayerId = rosterPlan.selectedDropPlayer.ids?.league_player;
    const planCandidateMlb = waiverPlayerForQueue.ids?.mlb;
    const planDropMlb = rosterPlan.selectedDropPlayer.ids?.mlb;
    if (!leaguePlayerId || !dropLeaguePlayerId || planCandidateMlb == null || planDropMlb == null) {
      setError('Cannot queue plan: missing identifiers on waiver or drop player.');
      setTimeout(() => setError(''), 5000);
      return null;
    }
    try {
      const res = await leaguesAPI.submitWaiverClaim(leagueId, {
        player_league_id: leaguePlayerId,
        drop_player_league_id: dropLeaguePlayerId,
        plan_fingerprint: rosterPlan.selectedPlan.plan_fingerprint,
        plan_candidate_player_id: planCandidateMlb,
        plan_drop_player_id: planDropMlb,
      });
      const data = res?.data || res;
      if (data?.success) {
        const playerName = waiverPlayerForQueue.info?.full_name || 'player';
        setSuccessMessage(`Waiver claim submitted for ${playerName}.`);
        setTimeout(() => setSuccessMessage(''), 5000);
        await loadData();
      } else {
        const msg = data?.error?.message || data?.message || 'Failed to submit waiver claim.';
        setError(msg);
        setTimeout(() => setError(''), 6000);
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error?.message || data?.detail || data?.message || err.message || 'Failed to submit waiver claim.';
      setError(msg);
      setTimeout(() => setError(''), 6000);
    }
    rosterPlan.reset();
    positionAssignment.setPlayerForAssignment(null);
    setIsWaiverPlanQueueMode(false);
    setWaiverPlayerForQueue(null);
    return { success: true };
  }, [waiverPlayerForQueue, rosterPlan, positionAssignment, leagueId, loadData]);

  const handleClaimConfirm = async (dropPlayerId) => {
    if (!playerPendingClaim) return;
    setShowClaimModal(false);

    const leaguePlayerId = playerPendingClaim.ids?.league_player;
    if (!leaguePlayerId) {
      setError('Cannot claim: player has no league_player id.');
      return;
    }

    try {
      const res = await leaguesAPI.submitWaiverClaim(leagueId, {
        player_league_id: leaguePlayerId,
        drop_player_league_id: dropPlayerId || null,
      });
      const data = res?.data || res;
      if (data?.success) {
        const playerName = playerPendingClaim.info?.full_name || 'player';
        setSuccessMessage(`Waiver claim submitted for ${playerName}.`);
        setTimeout(() => setSuccessMessage(''), 5000);
        await loadData();
      } else {
        // data.error is OBJECT in canonical envelope — read .message only.
        // Never let an object reach setError (React renders setError result as child).
        setError(
          data?.error?.message ||
          data?.message ||
          'Failed to submit waiver claim.'
        );
      }
    } catch (err) {
      // Canonical envelope from dynasty_dugout_exception_handler:
      //   { success: false, error: { message, type, details } }
      // Fallbacks: HTTPException-shaped { detail }, then axios err.message.
      const data = err.response?.data;
      const msg =
        data?.error?.message ||
        data?.detail ||
        data?.message ||
        err.message ||
        'Failed to submit waiver claim.';
      setError(msg);
    } finally {
      setPlayerPendingClaim(null);
    }
  };

  // ========================================
  // CANCEL CLAIM
  // ========================================
  const handleCancelClaim = async (claimId) => {
    setError('');
    setCancellingClaimId(claimId);
    try {
      await leaguesAPI.cancelWaiverClaim(leagueId, claimId);
      setSuccessMessage('Claim cancelled.');
      setTimeout(() => setSuccessMessage(''), 5000);
      await loadData();
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.error?.message ||
        data?.detail ||
        data?.message ||
        err.message ||
        'Failed to cancel waiver claim.';
      setError(msg);
    } finally {
      setCancellingClaimId(null);
    }
  };

  // ========================================
  // HELPERS
  // ========================================
  const isRestrictedUntilFuture = (restrictedUntil) => {
    if (!restrictedUntil) return false;
    return new Date(restrictedUntil) > new Date();
  };

  // My priority rank from priority_order
  const myPriorityEntry = (waivers?.priority_order || []).find(
    e => e.team_id === userTeamId
  );

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div className={dynastyTheme.components.section}>
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary}`} />
            <span className={dynastyTheme.classes.text.white}>Loading waivers...</span>
          </div>
        </div>
      </div>
    );
  }

  const playersOnWaivers = waivers?.players_on_waivers || [];
  const priorityOrder = waivers?.priority_order || [];

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div className="space-y-6">
      <TeamPageHeader
        title="Waivers"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
      />

      {/* Messages */}
      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </p>
        </div>
      )}
      {successMessage && (
        <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
          <p className={dynastyTheme.classes.text.success}>{successMessage}</p>
        </div>
      )}

      {/* ============================================
          SECTION 0 — My Active Claims (pending + failed)
          ============================================ */}
      {(myClaims?.claims || []).length > 0 && (
        <div className={`${dynastyTheme.components.card.base} p-0 overflow-hidden`}>
          <div className={`px-6 py-4 border-b ${dynastyTheme.classes.border.neutral}`}>
            <h3 className={`text-base font-semibold ${dynastyTheme.classes.text.white}`}>
              My Active Claims ({myClaims.claims.length})
            </h3>
          </div>
          <div className="divide-y divide-neutral-800">
            {myClaims.claims.map((claim) => {
              const isFailed = claim.status === 'failed';
              return (
                <div
                  key={claim.claim_id}
                  id={`claim-${claim.claim_id}`}
                  className={`flex items-center justify-between px-6 py-4 gap-4 ${
                    String(claim.claim_id) === String(highlightClaimId) ? 'ring-2 ring-yellow-400 rounded-lg' : ''
                  } ${
                    isFailed ? 'bg-red-500/5 border-l-2 border-red-500/50' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className={`text-sm font-medium ${dynastyTheme.classes.text.white} flex items-center gap-2`}>
                      {isFailed && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          FAILED
                        </span>
                      )}
                      {claim.player_name || 'Unknown player'}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-neutral-400">
                      <span>Priority #{claim.claim_priority}</span>
                      {claim.drop_player_name && (
                        <span>Drop: {claim.drop_player_name}</span>
                      )}
                      {claim.waiver_expires_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(claim.waiver_expires_at).toLocaleString(undefined, {
                            month: 'short', day: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                    {isFailed && (
                      <div className="mt-1 text-xs text-red-400">
                        System error during processing — commissioner notified, will retry on next run.
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleCancelClaim(claim.claim_id)}
                    disabled={cancellingClaimId === claim.claim_id}
                    className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'xs')} disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={isFailed ? 'Cancel this failed waiver claim' : 'Cancel this waiver claim'}
                  >
                    {cancellingClaimId === claim.claim_id ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============================================
          SECTION A — Players On Waivers
          ============================================ */}
      <div className={`${dynastyTheme.components.card.base} p-0 overflow-hidden`}>
        <div className={`px-6 py-4 border-b ${dynastyTheme.classes.border.neutral}`}>
          <h3 className={`text-base font-semibold ${dynastyTheme.classes.text.white}`}>
            Players On Waivers
          </h3>
        </div>

        {playersOnWaivers.length === 0 ? (
          <div className="py-10 text-center">
            <p className={dynastyTheme.classes.text.neutralLight}>No players on waivers.</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-800">
            {playersOnWaivers.map((player) => {
              const leaguePlayerId = player.ids?.league_player;
              const alreadyClaimed = !!player.league_context?.is_claimed_by_me;
              const waiverLabel =
                player.league_context?.availability?.waiver_label || '';
              const claimCount = player.claim_count ?? 0;
              const expiresAt =
                player.league_context?.availability?.waiver_expires_at || null;

              // Expired waivers can't accept new claims (service rejects with
              // WAIVER_PERIOD_EXPIRED). Disable the button so users don't click
              // something that will fail; the daily processor will clear the
              // player to FA shortly.
              const isExpired = expiresAt
                ? new Date(expiresAt).getTime() <= Date.now()
                : false;

              const fullName = player.info?.full_name || 'Unknown Player';
              const position = player.info?.position || '';
              const mlbTeam = player.info?.mlb_team || '';

              // Bible § 4.5.19: viewing team cannot claim a player they
              // just cut during the active waiver period (cooling off).
              const droppedByTeamId =
                player.league_context?.availability?.dropped_by_team_id;
              const droppedByMyTeam = !!droppedByTeamId && !!userTeamId
                && droppedByTeamId === userTeamId;
              const coolingOff = droppedByMyTeam && !isExpired;

              let buttonDisabled = false;
              let buttonLabel = 'Claim Player';
              let buttonTitle = 'Submit waiver claim';
              if (alreadyClaimed) {
                buttonDisabled = true;
                buttonLabel = 'Claimed';
                buttonTitle = 'Already claimed';
              } else if (coolingOff) {
                buttonDisabled = true;
                buttonLabel = 'Cooling Off';
                buttonTitle = 'You cut this player — wait for the waiver period to end before reclaiming';
              } else if (isExpired) {
                buttonDisabled = true;
                buttonLabel = 'Clearing soon';
                buttonTitle = 'Waiver period has expired — player will clear to free agency on the next processing run';
              }

              return (
                <div
                  key={leaguePlayerId || player.ids?.mlb}
                  className="flex items-center justify-between px-6 py-4 gap-4"
                >
                  {/* Left: player info */}
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Waiver label pill */}
                    {waiverLabel && (
                      <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        {waiverLabel}
                      </span>
                    )}
                    <div className="min-w-0">
                      <PlayerLink mlbId={player.ids?.mlb} name={fullName} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#f9fafb' }} />
                      <div className="flex items-center gap-2 mt-0.5">
                        {position && (
                          <span className={dynastyTheme.components.badge.warning}>
                            {position}
                          </span>
                        )}
                        {mlbTeam && (
                          <span
                            className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}
                          >
                            {mlbTeam}
                          </span>
                        )}
                        <span
                          className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}
                        >
                          {claimCount} claim{claimCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: expires + claim button */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {expiresAt && (
                      <div className={`flex items-center gap-1 text-xs ${isExpired ? 'text-red-400' : 'text-neutral-500'}`}>
                        <Clock className="w-3 h-3" />
                        <span>
                          {new Date(expiresAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    )}
                    <button
                      disabled={buttonDisabled}
                      onClick={() => handleClaimButtonClick(player)}
                      className={`${dynastyTheme.utils.getComponent(
                        'button',
                        buttonDisabled ? 'secondary' : 'primary',
                        'xs'
                      )} disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={buttonTitle}
                    >
                      {buttonLabel}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============================================
          SECTION B — Waiver Priority Order
          ============================================ */}
      <div className={`${dynastyTheme.components.card.base} p-0 overflow-hidden`}>
        <div className={`px-6 py-4 border-b ${dynastyTheme.classes.border.neutral}`}>
          <h3 className={`text-base font-semibold ${dynastyTheme.classes.text.white}`}>
            Waiver Priority Order
          </h3>
        </div>

        {priorityOrder.length === 0 ? (
          <div className="py-10 text-center">
            <p className={dynastyTheme.classes.text.neutralLight}>
              Priority order not available.
            </p>
          </div>
        ) : (
          <ol className="divide-y divide-neutral-800">
            {priorityOrder.map((entry) => {
              const isMyTeam = entry.team_id === userTeamId;
              const restricted = isRestrictedUntilFuture(entry.restricted_until);

              return (
                <li
                  key={entry.team_id}
                  className={`flex items-center gap-4 px-6 py-3 ${
                    isMyTeam
                      ? `${dynastyTheme.classes.bg.primaryLight} border-l-2 ${dynastyTheme.classes.border.primary}`
                      : ''
                  }`}
                >
                  <span
                    className={`text-sm font-mono w-7 text-right flex-shrink-0 ${
                      isMyTeam
                        ? dynastyTheme.classes.text.primary
                        : dynastyTheme.classes.text.neutralLight
                    }`}
                  >
                    #{entry.priority_rank || entry.rank || '?'}
                  </span>
                  <span
                    className={`flex-1 text-sm font-medium ${
                      isMyTeam
                        ? dynastyTheme.classes.text.primary
                        : dynastyTheme.classes.text.white
                    }`}
                  >
                    {entry.team_name || 'Unknown Team'}
                    {isMyTeam && (
                      <span
                        className={`ml-2 text-xs ${dynastyTheme.classes.text.neutralLight}`}
                      >
                        (you)
                      </span>
                    )}
                  </span>
                  {restricted && (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30 flex-shrink-0">
                      RESTRICTED
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* ============================================
          Claim Modal — Phase A.4.5 wired (parity with VirtualPlayerTable.js)
          isRosterFull/hasActiveSlot/hasBenchSlot come from canClaimWaiverPlayer
          which reads positionAssignment.rosterCapacityAnalysis. onRequestPlan
          opens RosterPlanModal in waiver-queue mode (DropDesignationModal's
          "Rearrange roster" button at L316-324 renders only when all three
          are passed AND hasActiveSlot===false).
          ============================================ */}
      {(() => {
        const claim = playerPendingClaim && positionAssignment.rosterCapacityAnalysis
          ? canClaimWaiverPlayer(playerPendingClaim, positionAssignment.rosterCapacityAnalysis)
          : null;
        return (
          <DropDesignationModal
            isOpen={showClaimModal}
            onClose={() => {
              setShowClaimModal(false);
              setPlayerPendingClaim(null);
            }}
            onConfirm={handleClaimConfirm}
            onRequestPlan={() => handleWaiverRequestPlan(playerPendingClaim)}
            playerBeingAdded={playerPendingClaim}
            currentRoster={positionAssignment.currentRoster}
            leagueId={leagueId}
            context="waiver_claim"
            isRosterFull={claim ? !claim.canClaim : !!playerPendingClaim?.league_context?.is_roster_full_for_my_team}
            hasActiveSlot={claim ? claim.hasActiveSlot : null}
            hasBenchSlot={claim ? claim.hasBenchSlot : null}
            waiverPriority={myPriorityEntry?.priority_rank ?? null}
            totalTeams={priorityOrder.length || null}
            waiverExpiresAt={
              playerPendingClaim?.league_context?.availability?.waiver_expires_at || null
            }
          />
        );
      })()}

      {/* Phase A.4.5: RosterPlanModal — parity with FreeAgentSearch.js:774-812.
          User picks a drop player + a rearrangement plan; onConfirm in
          waiver-queue mode submits the claim with plan_* fields (the queued
          plan persists to waiver_claims.roster_plan_steps; process_waiver_claims
          executes it atomically at award time via _execute_queued_plan). */}
      <RosterPlanModal
        candidate={positionAssignment.playerForAssignment}
        currentRoster={positionAssignment.currentRoster}
        state={rosterPlan.state}
        plans={rosterPlan.plans}
        selectedDropPlayer={rosterPlan.selectedDropPlayer}
        selectedPlan={rosterPlan.selectedPlan}
        errorClass={rosterPlan.errorClass}
        errorMessage={rosterPlan.errorMessage}
        infeasibleReason={rosterPlan.infeasibleReason}
        onPickDrop={rosterPlan.requestPlans}
        onChangeDrop={rosterPlan.changeDrop}
        onSelectPlan={rosterPlan.selectPlan}
        onConfirm={isWaiverPlanQueueMode ? handleWaiverPlanQueueConfirm : rosterPlan.executePlan}
        onCancel={() => {
          rosterPlan.cancel();
          rosterPlan.reset();
          positionAssignment.setPlayerForAssignment(null);
          if (isWaiverPlanQueueMode) {
            setIsWaiverPlanQueueMode(false);
            setWaiverPlayerForQueue(null);
          }
        }}
        onRetry={rosterPlan.retry}
        onDone={() => {
          rosterPlan.reset();
          positionAssignment.setPlayerForAssignment(null);
          if (isWaiverPlanQueueMode) {
            setIsWaiverPlanQueueMode(false);
            setWaiverPlayerForQueue(null);
          }
        }}
        isVisible={
          rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'
        }
      />
    </div>
  );
};

export default Waivers;
